<script lang="ts" setup>
import { computed, ref, toRef, watch } from "vue";
import type { AttemptPage, AttemptSummary } from "@/types";
import { useTransactionAttempt, useTransactionAttempts } from "@/queries/runs";
import KeysetPager from "@/components/KeysetPager.vue";
import TransactionMessageView from "./TransactionMessageView.vue";
import { elapsed, formatTimestamp, statusCodeBadge } from "../utils/transactionFormatters";

// Every attempt of a transaction, oldest first, ten to a page. A row opens to that attempt's request and response.
const props = defineProps<{
  runId: string;
  serviceId: string;
  transactionId: string;
  /** How many attempts the transaction made, for the "of N" on the range. */
  total: number;
}>();

const runId = toRef(props, "runId");
const serviceId = toRef(props, "serviceId");
const transactionId = toRef(props, "transactionId");

// --- Pages -----------------------------------------------------------------

// Keyset paging. `starts` is where each page began, so going back asks for that page again. The wire carries no
// position, so each start also keeps how many attempts came on the pages before it, which numbers the rows.
const starts = ref<{ after: string; before: number }[]>([{ after: "", before: 0 }]);
const start = computed(() => starts.value[starts.value.length - 1]);
const after = computed(() => start.value.after);

const { attemptPage, attemptsFetching, attemptsError, refetchAttempts } = useTransactionAttempts(
  runId,
  serviceId,
  transactionId,
  after,
);

// The page on screen and the count of attempts before it. The page before stays on screen while the next one loads,
// so the count is paired with a page when that page arrives, not read from the newest start.
const shown = ref<{ page: AttemptPage; before: number }>();
watch(
  attemptPage,
  (page) => {
    if (page) shown.value = { page, before: start.value.before };
  },
  { immediate: true },
);

const canPrev = computed(() => starts.value.length > 1);
const canNext = computed(() => !!shown.value?.page.next);

function prev() {
  opened.value = null;
  starts.value = starts.value.slice(0, -1);
}
function next() {
  const current = shown.value;
  if (!current?.page.next) return;
  opened.value = null;
  const before = current.before + current.page.items.length;
  starts.value = [...starts.value, { after: current.page.next, before }];
}

const rangeLabel = computed(() => {
  if (!shown.value) return "";
  const from = shown.value.before + 1;
  const to = shown.value.before + shown.value.page.items.length;
  return `${from}–${to} of ${props.total.toLocaleString()}`;
});

// --- The open attempt ------------------------------------------------------

// One row open at a time, named by its attempt's id.
const opened = ref<string | null>(null);
function isOpen(item: AttemptSummary) {
  return opened.value === item.id;
}
function toggle(item: AttemptSummary) {
  opened.value = isOpen(item) ? null : item.id;
}

const {
  attempt: openedRead,
  attemptFetching: openedFetching,
  attemptError: openedError,
  refetchAttempt: refetchOpened,
} = useTransactionAttempt(
  runId,
  serviceId,
  transactionId,
  opened,
);

// The read keeps the attempt before on screen while the next one loads, so an attempt shows only under its own
// row.
const openedAttempt = computed(() => {
  const read = openedRead.value;
  if (!read || read.id !== opened.value) return undefined;
  return read;
});
</script>

<template>
  <div>
    <p v-if="attemptsError && !attemptsFetching" class="small mb-0">
      <span class="text-danger">Couldn't load the attempts.</span>
      <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="refetchAttempts()">Retry</button>
    </p>
    <p v-else-if="!shown" class="small text-body-secondary mb-0">Loading…</p>
    <template v-else>
      <div class="table-responsive">
        <table class="table table-sm table-hover align-middle mb-0 attempts-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Sent</th>
              <th scope="col">Status</th>
              <th scope="col" class="text-end">Duration</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, index) in shown.page.items" :key="item.id">
              <tr
                data-attempt
                class="attempt-row"
                :class="{ 'table-active': isOpen(item) }"
                tabindex="0"
                :aria-expanded="isOpen(item)"
                @click="toggle(item)"
                @keydown.enter.prevent="toggle(item)"
                @keydown.space.prevent="toggle(item)"
              >
                <td class="text-body-secondary">{{ shown.before + index + 1 }}</td>
                <td>{{ formatTimestamp(item.sentAt) }}</td>
                <td>
                  <span v-if="item.statusCode" class="badge" :class="statusCodeBadge(item.statusCode)">
                    {{ item.statusCode }} {{ item.reasonPhrase }}
                  </span>
                  <span v-else-if="item.error" class="badge bg-danger" :title="item.error.message">Failed</span>
                </td>
                <td class="text-end">{{ elapsed(item.sentAt, item.receivedAt) }}</td>
              </tr>
              <tr v-if="isOpen(item)" data-opened class="attempt-opened">
                <td colspan="4" class="px-3 pt-2 pb-3 text-break">
                  <p v-if="openedError && !openedFetching" class="small mb-0">
                    <span class="text-danger">Couldn't load the attempt.</span>
                    <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="refetchOpened()">
                      Retry
                    </button>
                  </p>
                  <p v-else-if="!openedAttempt" class="small text-body-secondary mb-0">Loading…</p>
                  <template v-else>
                    <div class="small fw-medium mb-1">Request</div>
                    <dl class="row mb-0 small">
                      <TransactionMessageView
                        kind="request"
                        :message="openedAttempt.request"
                        :attempt-id="openedAttempt.id"
                        :run-id="runId"
                        :service-id="serviceId"
                        :transaction-id="transactionId"
                      />
                    </dl>
                    <div class="small fw-medium mt-2 mb-1">Response</div>
                    <dl v-if="openedAttempt.response" class="row mb-0 small">
                      <TransactionMessageView
                        kind="response"
                        :message="openedAttempt.response"
                        :attempt-id="openedAttempt.id"
                        :run-id="runId"
                        :service-id="serviceId"
                        :transaction-id="transactionId"
                      />
                    </dl>
                    <p v-else-if="openedAttempt.error" class="small text-danger mb-0">{{ openedAttempt.error.message }}</p>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <KeysetPager
        v-if="canPrev || canNext"
        :label="rangeLabel"
        :can-prev="canPrev"
        :can-next="canNext"
        :fetching="attemptsFetching"
        @prev="prev"
        @next="next"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.attempts-table { font-variant-numeric: tabular-nums; }
.attempt-row { cursor: pointer; }
// The opened row shows an attempt rather than offering one, so it takes no hover tint.
.attempt-opened { --bs-table-hover-bg: transparent; }
</style>
